import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { DateTime } from 'luxon';

import { Button, FormGroup, NavLinkButton } from '@shared/components/bootstrap';
import { Alert } from '@shared/components/bootstrap/alert';
import { Breadcrumb } from '@shared/components/bootstrap/breadcrumb';
import { Panel } from '@shared/components/bootstrap/panel';
import { Table } from '@shared/components/bootstrap/table';
import { InputFormControl } from '@shared/components/fields/input_form_control';
import { Currency, Picture } from '@shared/components/helpers';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { Preview as AddressPreview } from '@admin/components/address/preview';
import {
  useConfirmation,
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
} from '@admin/components/confirmation';
import { PageHeader } from '@admin/components/helpers/page_header';
import { Titleize } from '@admin/components/helpers/titleize';
import { Spinner } from '@admin/components/spinner';
import { accountURL, orderURL } from '@admin/config/routes';
import { useNavigate } from '@admin/hooks/use_navigate';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  type Barcode,
  type Item,
  type Image,
  type EasyPost__Shipment,
  type Shipping__ShipmentItemQuote,
  type EasyPost__Rate,
  type Account,
  type Order,
  useOrderShipFormQuery,
  useShipmentQuoteMutation,
  useShipmentShipMutation,
  Status,
  type Shipment__Input,
} from '@admin/schema';
import { hero } from '@admin/utils/hero';

type Size = {
  length?: string;
  width?: string;
  height?: string;
  weight?: string;
};

type Rate = Pick<
  EasyPost__Rate,
  'id' | 'service' | 'carrier' | 'rate' | 'currency' | 'mode' | 'deliveryDays' | 'deliveryDate'
>;

type Quote = Pick<EasyPost__Shipment, 'id'> & {
  rates: Array<Rate>;
};

type Entry = {
  item: Pick<Item, 'id' | 'state' | 'hero'> & {
    barcode: Pick<Barcode, 'id' | 'value'>;
    images: Array<Pick<Image, 'id' | 'source' | 'state'>>;
    shipmentItemQuote?: Pick<
      Shipping__ShipmentItemQuote,
      'carrier' | 'service' | 'deliveryDays' | 'deliveryDate'
    > | null;
  };
  size: Size;
  quote?: Quote;
  rate?: Rate;
};

const OrderShipFormFieldsEntryDelivery: React.FC<{
  deliveryDays?: number | null;
  deliveryDate?: string | null;
}> = ({ deliveryDays }) => <>({deliveryDays ?? 14} day delivery)</>;

const OrderShipFormFieldsEntrySize: React.FC<{
  entry: Entry;
  onSize(_: Size): void;
}> = ({ entry, onSize }) => (
  <>
    <FormGroup inline>
      <InputFormControl
        type="number"
        placeholder="Length (inches)"
        onChange={(event) => {
          const size = { ...entry.size, length: event.target.value };
          onSize(size);
        }}
        value={entry.size.length ?? ''}
      />
      <span> × </span>
      <InputFormControl
        type="number"
        placeholder="Width (inches)"
        onChange={(event) => {
          const changes = { ...entry.size, width: event.target.value };
          onSize(changes);
        }}
        value={entry.size.width ?? ''}
      />
      <span> × </span>
      <InputFormControl
        type="number"
        placeholder="Height (inches)"
        onChange={(event) => {
          const changes = { ...entry.size, height: event.target.value };
          onSize(changes);
        }}
        value={entry.size.height ?? ''}
      />
    </FormGroup>
    <FormGroup inline>
      <InputFormControl
        type="number"
        placeholder="Weight (pounds)"
        className="form-control"
        onChange={(event) => {
          const changes = { ...entry.size, weight: event.target.value };
          onSize(changes);
        }}
        value={entry.size.weight ?? ''}
      />
    </FormGroup>
  </>
);

const OrderShipFormFieldsEntryRate: React.FC<{
  rate: Rate;
  preferred?: boolean;
}> = ({ rate, preferred }) => (
  <span style={{ color: preferred ? 'green' : undefined }}>
    <span>
      {rate.carrier} - {rate.service}
    </span>
    <> </>
    <OrderShipFormFieldsEntryDelivery deliveryDays={rate.deliveryDays} deliveryDate={rate.deliveryDate} />
    <> - </>
    <Currency value={rate.rate} />
    <> </>
    {preferred && <>(Customer Selected Option)</>}
  </span>
);

const OrderShipFormFieldsEntryShipment: React.FC<{
  loading?: boolean;
  entry: Entry;
  onRate(_: Rate): void;
}> = ({ loading, entry, onRate }) => {
  if (loading) return <Spinner />;
  const shipment = entry.quote;
  if (!shipment) return null;
  const rates = shipment.rates;
  if (rates.length === 0) return <div>No rates available for selected dimensions / weight.</div>;

  const quote = entry.item.shipmentItemQuote;

  const unavailable = quote && !rates.some((rate) => rate.service === quote.service);

  return (
    <div>
      {unavailable && (
        <Alert style="warning">
          The customer selected {quote.carrier} - {quote.service}
          <OrderShipFormFieldsEntryDelivery deliveryDays={quote.deliveryDays} deliveryDate={quote.deliveryDate} />. This
          option is no longer available. Please choose another similar shipping option.
        </Alert>
      )}

      {rates.map((rate) => (
        <div key={rate.id}>
          <label>
            <input
              type="radio"
              name={`rate-${entry.item.id}`}
              value={rate.id}
              checked={entry.rate?.id === rate.id}
              onChange={(event) => {
                if (!event.target.checked) return;
                onRate(rate);
              }}
            />{' '}
            <OrderShipFormFieldsEntryRate rate={rate} preferred={rate.service === quote?.service} />
          </label>
        </div>
      ))}
    </div>
  );
};

const OrderShipFormFieldsEntry: React.FC<{
  order: Pick<Order, 'id'>;
  account: Pick<Account, 'id'>;
  entry: Entry;
  onSize(_: Size): void;
  onQuote(_: Quote): void;
  onRate(_: Rate): void;
}> = ({ account, order, entry, onSize, onQuote, onRate }) => {
  const [loading, setLoading] = useState(false);
  const request = useRef(0); // used to ensure the only the latest request is quoted
  const [quote] = useShipmentQuoteMutation({ client });

  const calculate = async ({ length, width, height, weight }: Size) => {
    if (!length || !width || !height || !weight) return;
    request.current++;
    const requestID = request.current;

    setLoading(true);
    const { data } = await quote({
      variables: {
        accountID: account.id,
        orderID: order.id,
        itemID: entry.item.id,
        width: Number(width),
        length: Number(length),
        height: Number(height),
        weight: Number(weight),
      },
    });
    setLoading(false);

    if (requestID !== request.current) return;
    const shipment = data?.quote?.shipment;
    if (!shipment) return;

    onQuote(shipment);
  };

  return (
    <tr>
      <td className="text-left col-zero col-nowrap">
        <figure className="brick-square">
          <Picture
            className="brick-image"
            source={hero(entry.item)?.source}
            width={240}
            height={240}
            background={ImgixBG.White}
            fit={ImgixFit.Fill}
          />
        </figure>
        <div className="text-center">
          <span>#{entry.item.barcode.value}</span>
          <span> </span>
          <strong>({entry.item.state})</strong>
        </div>
      </td>
      <td className="text-left col-sm-6 col-nowrap">
        <OrderShipFormFieldsEntrySize
          entry={entry}
          onSize={(size) => {
            onSize(size);
            calculate(size);
          }}
        />
      </td>
      <td className="text-left col-sm-4 col-nowrap">
        <OrderShipFormFieldsEntryShipment loading={loading} entry={entry} onRate={onRate} />
      </td>
      <td className="text-right col-sm-2 col-nowrap">
        <strong>{entry.rate ? <Currency value={entry.rate.rate} /> : '-'}</strong>
      </td>
    </tr>
  );
};

const OrderShipFormFields: React.FC<{
  account: Pick<Account, 'id'>;
  order: Pick<Order, 'id'>;
  entries: readonly Entry[];
  onSize(index: number, size: Size): void;
  onQuote(index: number, quote: Quote): void;
  onRate(index: number, rate: Rate): void;
}> = ({ account, order, entries, onSize, onQuote, onRate }) => {
  const total = entries.reduce((acc, entry) => acc + (entry.rate?.rate ?? 0), 0);

  return (
    <Table responsive>
      <tbody>
        {entries.map((entry, index) => (
          <OrderShipFormFieldsEntry
            key={entry.item.id}
            account={account}
            order={order}
            entry={entry}
            onSize={(size) => onSize(index, size)}
            onQuote={(shipment) => onQuote(index, shipment)}
            onRate={(rate) => onRate(index, rate)}
          />
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td className="text-left col-zero col-nowrap" />
          <td className="text-left col-sm-6 col-nowrap" />
          <td className="text-left col-sm-4 col-nowrap" />
          <td className="text-right col-sm-2 col-nowrap">
            <strong>
              Total: <Currency value={total} />
            </strong>
          </td>
        </tr>
      </tfoot>
    </Table>
  );
};

export const OrderShipForm: React.FC = () => {
  useTitle('Ship');
  const { accountID, orderID } = useParams<{ accountID: string; orderID: string }>();
  const navigate = useNavigate();
  const { confirmation, confirm } = useConfirmation();
  const [ship, { loading: saving, data: shipment }] = useShipmentShipMutation({ client });
  const { data } = useOrderShipFormQuery({ client, variables: { accountID, orderID } });
  const [entries, setEntries] = useState<Entry[]>([]);
  const error = shipment?.ship?.error;

  useEffect(() => {
    if (!data) return;
    const items = data.account.order.items;

    setEntries(
      items.map((item) => ({
        item,
        size: {},
        quote: undefined,
        rate: undefined,
      })),
    );
  }, [data]);

  if (!data) return <Spinner />;

  const account = data.account;
  const order = account.order;

  const shippable = entries.every(
    (entry) =>
      entry.quote && entry.rate && entry.size.length && entry.size.width && entry.size.height && entry.size.weight,
  );

  const onSize = async (index: number, size: Size) => {
    setEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, size } : entry)));
  };

  const onQuote = async (index: number, quote: Quote) => {
    setEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, quote } : entry)));
  };

  const onRate = async (index: number, rate: Rate) => {
    setEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, rate } : entry)));
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!shippable) return;
    if (saving) return;

    const address = `${order.address.street} ${order.address.aptsuite} ${order.address.city} ${order.address.state} ${order.address.zip}`;

    const confirmed = await confirm({
      title: 'Are you sure?',
      description: (
        <>
          <p>Please verify that the shipping address ({address}) and dimensions / weight are correct.</p>
          <p>Shipping an order purchases postage and cannot be undone.</p>
          <p>If you are not sure, contact your manager prior to shipping.</p>
        </>
      ),
      confirm: 'Verified, Ship This Order',
      cancel: 'Whoops, I Forgot Something',
    });

    if (confirmed !== ConfirmationResponse.Confirm) return;

    const shipments: Shipment__Input[] = entries.map((entry) => ({
      itemID: entry.item.id,
      shipmentID: entry.quote?.id ?? '',
      rateID: entry.rate?.id ?? '',
      carrier: entry.rate?.carrier ?? '',
      service: entry.rate?.service ?? '',
      rate: entry.rate?.rate ?? 0,
      currency: entry.rate?.currency ?? 'USD',
      width: Number(entry.size.width ?? 0),
      length: Number(entry.size.length ?? 0),
      height: Number(entry.size.height ?? 0),
      weight: Number(entry.size.weight ?? 0),
    }));

    const response = await ship({ variables: { accountID, orderID, shipments } });
    if (response.data?.ship?.status !== Status.Ok) return;

    navigate(orderURL({ id: order.id, account: { id: account.id } }));
  };

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}

      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/accounts">Accounts</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to={accountURL({ id: account.id })}>
              #{account.number} ({account.customer.name})
            </NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to={orderURL({ id: order.id, account: { id: account.id } })}>#{order.number}</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Ship</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>

      <form role="form" onSubmit={onSubmit}>
        <fieldset disabled={saving}>
          <Panel>
            <Panel.Header>
              <h3 className="panel-title">Details</h3>
            </Panel.Header>
            <Panel.Body>
              <div>
                <strong>Kind:</strong> <Titleize text={order.type} /> - <Titleize text={order.subtype} />
              </div>
              <div>
                <strong>Scheduled:</strong>{' '}
                {DateTime.fromISO(order.scheduled, { zone: order.region.tz }).toLocaleString(DateTime.DATETIME_MED)}
              </div>
              <div>
                <strong>Address:</strong>
                <AddressPreview address={order.address} />
              </div>
              <div>
                <strong>State:</strong> {order.state}
              </div>
            </Panel.Body>
          </Panel>

          <Panel>
            <Panel.Body>
              {error && <Alert style="danger">{error}</Alert>}

              <OrderShipFormFields
                account={account}
                order={order}
                entries={entries}
                onSize={onSize}
                onQuote={onQuote}
                onRate={onRate}
              />
            </Panel.Body>
            <Panel.Footer align="right">
              <NavLinkButton to={orderURL({ id: order.id, account: { id: account.id } })}>Cancel</NavLinkButton>
              <Button type="submit" kind="primary" disabled={!shippable}>
                Save
              </Button>
            </Panel.Footer>
          </Panel>
        </fieldset>
      </form>
    </>
  );
};
